import { tocData } from '@/modules/products/ProductDocumentPage/components/TableOfContent/tableOfContent.model';
import Link from 'next/link';
import { useRouter } from 'next/router';
import styles from '../tableOfContent.module.scss';

/**
 * Props for the TocMenuItem component.
 */
interface TocMenuItemProps {
  item: tocData<string>;
  maxDepth: number;
  activeAnchor: string;
  onItemClick: (e: React.MouseEvent<HTMLAnchorElement>, tocLink: string) => void;
}

/**
 * TocMenuItem component to render a single TOC item.
 * @param props - Props for the component.
 * @returns JSX.Element
 */
function TocMenuItem({
  item,
  maxDepth,
  activeAnchor,
  onItemClick,
}: TocMenuItemProps): JSX.Element {
  const router = useRouter();
  const hasChildren = item.children && item.children.length > 0;
  const children = item.children;

  let tocLink: string = item.value.replace(/ /g, '-');
  tocLink = tocLink.replace(/[(),:\/}{]/g, '');
  tocLink = tocLink.toLowerCase();

  const isActive = tocLink === activeAnchor;
  const hrefStyle = isActive
    ? `${styles.selectedTableOfContentTitle} ${styles.tableOfContentChildren}`
    : styles.tableOfContentChildren;

  /**
   * Handles the click event for a TOC item.
   * Updates the URL with the selected anchor tag.
   * @param e - The click event.
   */
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault();
    onItemClick(e, tocLink);

    // Update the URL with the anchor tag
    router.push(`#${tocLink}`, undefined, { shallow: true });
  };

  return (
    <>
      <li key={`${tocLink}-${item.depth}`} data-id={tocLink}>
        <Link href={`#${tocLink}`}>
          <a
            href={`#${tocLink}`}
            data-testid={`tocLink`}
            onClick={handleClick}
            className={hrefStyle}
          >
            {item.value}
          </a>
        </Link>
      </li>
      {hasChildren && item.depth <= maxDepth && (
        <li>
          <ul className={styles.childrenContainer}>
            {children?.map((child) => (
              <TocMenuItem
                key={child.id}
                item={child}
                onItemClick={onItemClick}
                activeAnchor={activeAnchor}
                maxDepth={maxDepth}
              />
            ))}
          </ul>
        </li>
      )}
    </>
  );
}

export default TocMenuItem;
